import { Link, useForm } from "@inertiajs/react";
import type { FormEvent } from "react";
import type { AuthMethod, Persona, PersonaFormData } from "@/types";
import AuthOption from "./forms/AuthOption";
import SelectField from "./forms/SelectField";
import TextArea from "./forms/TextArea";
import TextField from "./forms/TextField";

type PersonaFormProps = {
  cancelHref: string;
  formTitle: string;
  method: "post" | "patch";
  persona: Persona;
  submitLabel: string;
  url: string;
};

const authMethods: Array<{ label: string; value: AuthMethod }> = [
  { label: "No authentication needed", value: "none" },
  { label: "Username & Password", value: "credentials" },
  { label: "Custom Authentication Instructions", value: "instructions" },
];

const techLevels = ["", "Beginner", "Intermediate", "Expert", "Varies"];

export default function PersonaForm({ cancelHref, formTitle, method, persona, submitLabel, url }: PersonaFormProps) {
  const { data, errors, processing, setData, submit } = useForm<PersonaFormData>({
    age_range: persona.age_range ?? "",
    auth_instructions: persona.auth_instructions ?? "",
    auth_method: persona.auth_method ?? "none",
    auth_password: persona.auth_password ?? "",
    auth_username: persona.auth_username ?? "",
    country: persona.country ?? "",
    description: persona.description ?? "",
    language: persona.language ?? "",
    name: persona.name ?? "",
    occupation: persona.occupation ?? "",
    tech_level: persona.tech_level ?? "",
  });

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    submit(method, url);
  }

  return (
    <form onSubmit={handleSubmit} className="card">
      <div className="px-6 pt-6 pb-2">
        <h2 className="text-lg font-semibold">{formTitle}</h2>
        <p className="mt-2 text-sm text-gray-600">
          Create or edit a persona to represent a user type for your interactions.
        </p>
      </div>

      <div className="card-body space-y-8">
        <section>
          <h2 className="mb-4 border-b border-gray-200 pb-2 text-lg font-semibold text-gray-800">Basic Information</h2>
          <div className="grid gap-6 md:grid-cols-2">
            <TextField
              error={errors.name}
              label="Persona Name"
              onChange={(value) => setData("name", value)}
              required
              value={data.name}
            />
            <SelectField
              label="Technical Proficiency"
              onChange={(value) => setData("tech_level", value)}
              options={techLevels}
              value={data.tech_level}
            />
          </div>
          <TextArea
            error={errors.description}
            label="Persona Description"
            onChange={(value) => setData("description", value)}
            required
            value={data.description}
          />
        </section>

        <section>
          <h2 className="mb-4 border-b border-gray-200 pb-2 text-lg font-semibold text-gray-800">Demographics</h2>
          <div className="grid gap-6 md:grid-cols-2">
            <TextField
              label="Age Range"
              onChange={(value) => setData("age_range", value)}
              placeholder="e.g., 25-40"
              value={data.age_range}
            />
            <TextField
              error={errors.country}
              label="Country"
              onChange={(value) => setData("country", value)}
              placeholder="e.g., United States"
              required
              value={data.country}
            />
            <TextField
              error={errors.language}
              label="Primary Language"
              onChange={(value) => setData("language", value)}
              placeholder="e.g., English"
              required
              value={data.language}
            />
            <TextField
              error={errors.occupation}
              label="Occupation"
              onChange={(value) => setData("occupation", value)}
              placeholder="e.g., Teacher"
              value={data.occupation}
            />
          </div>
        </section>

        <section>
          <h2 className="mb-4 border-b border-gray-200 pb-2 text-lg font-semibold text-gray-800">
            Authentication Settings
          </h2>
          <p className="mb-4 text-gray-600">
            Does this persona have login credentials that should be used for interactions?
          </p>
          <div className="space-y-3">
            {authMethods.map((authMethod) => (
              <AuthOption
                key={authMethod.value}
                label={authMethod.label}
                onSelect={() => setData("auth_method", authMethod.value)}
                selected={data.auth_method === authMethod.value}
              />
            ))}
          </div>

          {data.auth_method === "credentials" && (
            <div className="mt-4 grid gap-6 md:grid-cols-2">
              <TextField
                error={errors.auth_username}
                label="Username"
                onChange={(value) => setData("auth_username", value)}
                value={data.auth_username}
              />
              <TextField
                error={errors.auth_password}
                label="Password"
                onChange={(value) => setData("auth_password", value)}
                type="password"
                value={data.auth_password}
              />
            </div>
          )}

          {data.auth_method === "instructions" && (
            <TextArea
              error={errors.auth_instructions}
              label="Authentication Instructions"
              onChange={(value) => setData("auth_instructions", value)}
              placeholder="Explain how to authenticate on websites..."
              value={data.auth_instructions}
            />
          )}
        </section>

        <div className="flex flex-col-reverse justify-end gap-3 lg:flex-row">
          <Link href={cancelHref} className="btn">
            Cancel
          </Link>
          <button className="btn btn-primary min-w-48" disabled={processing} type="submit">
            {processing ? "Saving..." : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
}
